import type { ReactNode } from "react";
import { HoverInfo } from "../basic/Tooltip";
import { css } from "../../../../styled-system/css";
import Skeleton from "react-loading-skeleton";

interface Props {
  name: string;
  value: ReactNode;
  center?: boolean;
  toolTip?: string;
  isLoading?: boolean;
  comparisonDisplay?: ReactNode;
}

/**
 * LargeValue - Displays a labeled value (non-metric display)
 *
 * Use this component for displaying status, dates, text labels, and other
 * non-numeric values that don't require metric formatting or comparison logic.
 *
 * For numeric metrics with formatting and comparison features, use DetailedMetric instead.
 *
 * @example
 * <LargeValue
 *   name="State"
 *   value="Active"
 *   center
 * />
 *
 * @example
 * <LargeValue
 *   name="Start Date"
 *   value="January 1, 2025"
 * />
 */
const LargeValue = ({ name, value, center = false, toolTip, isLoading = false, comparisonDisplay }: Props) => {
  return (
    <div>
      <div
        className={css({
          textAlign: { base: "center", md: center ? "center" : "end" },
        })}
      >
        <span
          className={css({
            display: "flex",
            gap: "xsmall",
            justifyContent: { base: "center", md: center ? "center" : "end" },
            alignItems: "center",
            color: "text.label",
            fontSize: "large",
            fontWeight: 500,
            lineHeight: "1",
          })}
        >
          {name}
          {toolTip && <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>{toolTip}</HoverInfo>}
        </span>
      </div>
      <div
        className={css({
          textAlign: { base: "center", md: center ? "center" : "end" },
        })}
      >
        <h3
          className={css({
            marginTop: "0.2rem",
            marginBottom: "0.2rem",
            fontSize: "xxlarge",
            fontWeight: "600",
          })}
        >
          {isLoading ?
            <Skeleton width="80px" />
          : value}
        </h3>
        <div className={css({ minHeight: "20px" })}>
          <small>{comparisonDisplay || " "}</small>
        </div>
      </div>
    </div>
  );
};

export default LargeValue;
